import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  InputAdornment,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";

import * as Yup from "yup";
import { Formik } from "formik";

import ModalLoadingFull from "../../components/modals/ModalLoadingFull";
import { postNotification } from "../../components/utilities/Notifications";
import { useUpdateCart } from "../../components/user/BCPAuthReducers";
import { addToCart } from "../../components/user/utils";
import { BCPMinus, BCPPlus } from "../../components/icons/bcp/BCPIcons";
import { getData } from "../../components/utilities/UtilityFunctions";

const fetchFile = async (fileId) => {
  if (!fileId) return;

  var returnObj;
  try {
    returnObj = await getData(`/files/${fileId}`, {
      queryStringParameters: {},
    });
  } catch (err) {
    console.error(err);
  }
  return returnObj;
};

const ImagePreview = (props) => {
  const { fileId, alt, outerSX } = props;
  const [loading, setLoading] = useState(true);
  const [image, setImage] = useState(null);
  useEffect(() => {
    const go = async () => {
      setLoading(true);
      if (!fileId) {
        setImage(null);
        setLoading(false);
        return;
      }

      if (image) {
        if (image.id === fileId) {
          setLoading(false);
          return;
        }
        setImage(null);
      }

      try {
        var file = await fetchFile(fileId);
        if (file) {
          setImage({
            id: fileId,
            ...file,
          });
        }
      } catch (err) {}
      setLoading(false);
    };

    go();
    // eslint-disable-next-line
  }, [fileId]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "50px",
        minHeight: "50px",
        maxHeight: "50px",
        p: 1,
        border: 1,
        borderColor: "divider",
        ...outerSX,
      }}
    >
      {!loading && image?.url && (
        <img
          src={image.url}
          alt={alt}
          style={{ maxWidth: "100%", maxHeight: "100%" }}
        />
      )}

      {loading && <CircularProgress size={24} />}
    </Box>
  );
};

export default function StoreProduct() {
  const useUpdateCartReducer = useUpdateCart();
  const navigate = useNavigate();

  let { productId } = useParams();

  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState(null);

  useEffect(() => {
    const go = async () => {
      try {
        var apiProduct = await getData(`/products/${productId}`, {
          queryStringParameters: {},
        });
        setProduct(apiProduct);
      } catch (err) {
        postNotification("error", err);
        navigate("/home");
        setLoading(false);
        return;
      }
      setLoading(false);
    };

    go();
    // eslint-disable-next-line
  }, [productId]);

  return (
    <>
      {!loading && product && (
        <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
          <Grid container sx={{ gap: 2 }}>
            <Grid item xs={12} sx={{ p: 2 }}>
              <Typography variant="h3">{product?.name}</Typography>
            </Grid>

            <Grid item xs={12} sx={{ p: 2 }}>
              <Grid container spacing={1}>
                {product?.imageFileId && (
                  <Grid item xs={12} sm={6} lg={8}>
                    <ImagePreview
                      fileId={product?.imageFileId}
                      alt={product?.name}
                      outerSX={{
                        width: "100%",
                        minHeight: "unset",
                        maxHeight: "unset",
                        border: "unset",
                        borderColor: "unset",
                      }}
                    />
                  </Grid>
                )}
                <Grid
                  item
                  xs={12}
                  sm={product?.imageFileId ? 6 : 12}
                  lg={product?.imageFileId ? 4 : 12}
                  sx={{ mt: [1, 3] }}
                >
                  <Typography variant="h4">{product.name}</Typography>
                  <Typography variant="body1">
                    {product.loyaltyPoints} LP
                  </Typography>
                  {product.stockStatus !== "In Stock" && (
                    <Typography variant="body1">
                      {product.stockStatus}
                    </Typography>
                  )}
                  {product.stockStatus !== "Out of Stock" && (
                    <Formik
                      initialValues={{
                        qty: 1,
                      }}
                      validationSchema={Yup.object().shape({
                        qty: Yup.number().min(1).required("Required"),
                      })}
                      onSubmit={(values) => {
                        addToCart(product, values.qty, useUpdateCartReducer);
                      }}
                    >
                      {({
                        errors,
                        handleBlur,
                        handleChange,
                        handleSubmit,
                        touched,
                        values,
                        setFieldValue,
                      }) => (
                        <form onSubmit={handleSubmit} noValidate>
                          <Grid container sx={{ gap: 2 }}>
                            <Grid item xs={12}>
                              <TextField
                                name="qty"
                                label="Quantity"
                                value={values.qty}
                                variant="outlined"
                                fullWidth
                                required
                                margin="normal"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={Boolean(touched.qty && errors.qty)}
                                helperText={touched.qty && errors.qty}
                                type="number"
                                inputProps={{
                                  min: 1,
                                  style: { textAlign: "center" },
                                }}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        aria-label="Plus 1"
                                        edge="end"
                                        color="primary"
                                        onClick={() => {
                                          setFieldValue("qty", values.qty + 1);
                                        }}
                                      >
                                        <BCPPlus />
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <IconButton
                                        aria-label="Minus 1"
                                        edge="end"
                                        color="primary"
                                        onClick={() => {
                                          setFieldValue("qty", values.qty - 1);
                                        }}
                                        disabled={values.qty <= 1}
                                      >
                                        <BCPMinus />
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <Button
                                fullWidth
                                color="primary"
                                variant="contained"
                                type="submit"
                              >
                                Add to Cart
                              </Button>
                            </Grid>
                          </Grid>
                        </form>
                      )}
                    </Formik>
                  )}
                </Grid>
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                p: 2,
              }}
            >
              <Typography variant="h4">Details</Typography>
              <Grid container spacing={1} sx={{ mt: 1 }}>
                {product.description && product.description !== "" && (
                  <Grid item xs={12}>
                    <Typography variant="body1" gutterBottom paragraph>
                      {product.description.split("\n").map((i, key) => {
                        return <Box key={key}>{i}</Box>;
                      })}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  {product.categories?.length > 0 &&
                    product.categories.map((category, index) => {
                      return (
                        <Chip
                          label={category}
                          component="a"
                          href={`/shop/categories/${category}`}
                          variant="outlined"
                          clickable
                          sx={{ mr: 1 }}
                        />
                      );
                    })}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      )}
      {loading && <ModalLoadingFull />}
    </>
  );
}
